import React, { ReactElement } from "react";
import { AgGridReact, Button, HelpIndicator, Tag } from "@jsq/ds/core";
import ActionsCellRenderer, {
  EntityActionRendererParamsProps,
} from "~/pages/FundAccounting/Entities/components/CellRenderers/Actions";
import HeaderCellRenderer, {
  HeaderRendererParamProps,
} from "~/pages/FundAccounting/Entities/components/CellRenderers/Header";
import { SPACING } from "~/styles/variables";
import { TransactionCategory } from "../types";
import { actionColumn, iconButton } from "./styles_vanilla.css";

export const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  autoSizeStrategy: {
    type: "fitGridWidth",
  },
  suppressCellFocus: true,
  // This is for better place the action button
  rowHeight: 44,
  animateRows: false,
  // This is to wrap long text
  defaultColDef: {
    wrapText: true,
    autoHeight: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  suppressMenuHide: true,
  suppressContextMenu: true,
  treeData: true,
  popupParent: document.querySelector("body"),
  autoGroupColumnDef: {
    headerName: "Transaction category",
    minWidth: 280,
    cellRendererParams: {
      suppressCount: true,
    },
    sortable: false,
    filter: true,
    filterParams: {
      values: [
        TransactionCategory.CapitalCalls,
        TransactionCategory.Redemptions,
        TransactionCategory.Distributions,
        TransactionCategory.NetIncome,
      ],
    },
    menuTabs: ["filterMenuTab"],
    cellStyle: { borderRightColor: "#e5e4e0" },
  },
  getDataPath: (data: ColumnItemType) => {
    return data.transactionCategory;
  },
};

export type IntegrationStartProps = {
  text: string;
  onClick?: () => void;
};

type IntegrationStartCellRendererProps = {
  data: {
    integrationStart: IntegrationStartProps;
  };
};

export type GlSyncError = {
  label: string;
  modalMessage: string;
  errorMessages: string[] | null | undefined;
};

export type ColumnItemType = {
  transactionCategory: string[];
  integrationStart: IntegrationStartProps;
  integrationThrough: string;
  lastJSQUpdate: React.ReactNode | null;
};

export type EntityGlSyncDetail = {
  glAsOfDate: string | null;
  glStartDate: string | null;
  lastSyncAt: string | null;
  active: boolean;
  lastSyncUserId: number | null;
  lastSyncUserName: string | null;
  newBatchCount?: number | null;
  syncedGlBatchCount?: number | null;
  revisedBatchCount?: number | null;
};

const IntegrationStartTooltip =
  "GL integration start date is the earliest GL date used to create JSQ transactions via the GL integration";
const IntegrationThroughTooltip =
  "GL integration through date is the latest GL date used to create JSQ transactions via the GL integration";

// TODO: update this tooltip content to include user once we have query for that.
const LastJSQUpdateTooltip =
  "Last JSQ sync shows the status of the last sync of JSQ transactions via the GL integration (e.g. updating) and includes the date, time and user once complete.";

export type TransactionCategoryActionProps = {
  data: {
    actions: {
      isEntity: boolean;
      isSyncing: boolean;
      isEntityBlocked?: boolean;
      settings: EntityGlSyncDetail;
      onActiveGLIntegration: () => void;
      onUpdateJSQTxn: () => void;
    };
  };
};

type Props = {
  params: TransactionCategoryActionProps;
};

const TransactionCategoryAction = ({ params }: Props): ReactElement | null => {
  const { isSyncing, isEntityBlocked, settings, onActiveGLIntegration, onUpdateJSQTxn } =
    params.data.actions;
  if (isEntityBlocked || !settings) {
    return null;
  }

  if (settings.active) {
    const helpText = isSyncing ? "Sync in progress..." : "Sync JSQ transactions";
    return (
      <HelpIndicator id="syncHelp" placement="top" content={helpText}>
        <span className="d-inline-block">
          <Button
            icon="sync-alt"
            variant="muted"
            onClick={() => {
              if (!isEntityBlocked) {
                onUpdateJSQTxn();
              }
            }}
            className={iconButton}
            disabled={isSyncing}
          ></Button>
        </span>
      </HelpIndicator>
    );
  } else {
    // Sync disabled, allow user to access configs to enable sync settings.
    return (
      <HelpIndicator
        id="configureHelp"
        placement="top"
        content="Configure integration settings"
      >
        <Button
          icon="cog"
          variant="muted"
          onClick={() => {
            if (!isEntityBlocked) {
              onActiveGLIntegration();
            }
          }}
          className={iconButton}
        ></Button>
      </HelpIndicator>
    );
  }
};

export const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "integrationStart",
    headerName: "Integration start",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} tooltipContent={IntegrationStartTooltip} />;
    },
    cellRenderer: (params: IntegrationStartCellRendererProps) => {
      const data = params.data.integrationStart;
      if (data?.onClick) {
        return (
          <Tag
            onClick={data?.onClick}
            variant="primary"
            style={{ marginBottom: SPACING.X_SMALL, cursor: "pointer" }}
          >
            Transfers enabled
          </Tag>
        );
      }
      return <>{data?.text || ""}</>;
    },
    minWidth: 185,
  },
  {
    field: "integrationThrough",
    headerName: "Integration through",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} tooltipContent={IntegrationThroughTooltip} />;
    },
    minWidth: 165,
  },
  {
    field: "lastJSQUpdate",
    headerName: "Last JSQ sync",
    headerComponent: (params: HeaderRendererParamProps) => {
      return <HeaderCellRenderer params={params} tooltipContent={LastJSQUpdateTooltip} />;
    },
    cellRenderer: (params: HeaderRendererParamProps) => {
      if (params.value === "Syncing…") {
        return (
          <Tag variant="neutral" style={{ marginBottom: SPACING.X_SMALL }}>
            {params.value}
          </Tag>
        );
      }
      return params.value; // return just the time stamp
    },
    minWidth: 170,
  },
  {
    field: "actions",
    headerName: "",
    suppressHeaderMenuButton: true,
    cellRenderer: (
      params: EntityActionRendererParamsProps | TransactionCategoryActionProps,
    ) => {
      const isEntity = params.data.actions.isEntity;

      const transactionAction = (
        <TransactionCategoryAction params={params as TransactionCategoryActionProps} />
      );

      const action = isEntity ? (
        <ActionsCellRenderer params={params as EntityActionRendererParamsProps} />
      ) : (
        transactionAction
      );

      return <div className={actionColumn}>{action}</div>;
    },
  },
];
